import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExtensionIcon from "@mui/icons-material/Extension";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../../services/GoogleAnalytics";
import { getAppCopyrightLine } from "../../config/appCopyright";
import { usePagesTranslation } from "../locales";
import SocialShareDropdown, {
    buildStorySharePageUrl,
} from "../puzzles/common/components/SocialShareDropdown";
import { ACCENT } from "../../dark-theme/palette";
import { playPillButtonSx } from "../../dark-theme/surfaces";

/**
 * Story card above clues on `/story/play-cross/:id`. Body `children` is usually word-find prose with numbered circles only where puzzle words were.
 *
 * @param {{
 *   summaryTitle: string,
 *   imageUrl?: string,
 *   children?: React.ReactNode,
 *   storyShareId?: string,
 *   storyShareTitle?: string,
 *   storySharePuzzleType?: string,
 * }} props — `summaryTitle` is usually the localized story headline; falls back to generic "Story" from callers when empty.
 * Accordion open/closed state is not persisted (starts collapsed each visit).
 */
export default function StoryPlayStoryAccordion({
    summaryTitle,
    imageUrl,
    children,
    storyShareId,
    storyShareTitle,
    storySharePuzzleType,
    storyId,
    showSlidingPuzzle = false,
    showJigsawPuzzle = false,
    defaultExpanded = false,
    /** Smaller header bar (e.g. crossword clues sidebar). */
    compact = false,
}) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [storyImageDialogOpen, setStoryImageDialogOpen] = useState(false);
    const genericHeading = String(t("puzzle.playCross.story-card-heading")).trim();
    const raw = String(summaryTitle ?? "").trim();
    const prefixLabel = String(t("puzzle.playCross.story-title-prefix", "Story:"));
    const spacer = prefixLabel.endsWith(" ") ? "" : " ";
    /** Avoid "Story: Story" when callers pass the generic fallback as `summaryTitle`. */
    const headingText =
        !raw || raw === genericHeading ? genericHeading : `${prefixLabel}${spacer}${raw}`;

    const storyShareUrl = useMemo(() => {
        if (!storyShareId) return "";
        return buildStorySharePageUrl(storyShareId) || "";
    }, [storyShareId]);

    const shareLabel = useMemo(() => {
        const label = String(storyShareTitle ?? "").trim();
        return label || genericHeading;
    }, [storyShareTitle, genericHeading]);

    const showStoryShare =
        Boolean(storyShareUrl && storyShareId && storySharePuzzleType);

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
            disableGutters
            elevation={compact ? 1 : 2}
            sx={{
                flexShrink: 0,
                borderRadius: compact ? 1.5 : 2,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon aria-hidden sx={{ fontSize: compact ? 20 : 24 }} />
                }
                aria-controls="story-play-accordion-content"
                id="story-play-accordion-header"
                sx={{
                    minHeight: compact ? 36 : 48,
                    px: compact ? 1.25 : 2,
                    py: compact ? 0 : undefined,
                    "&.Mui-expanded": {
                        minHeight: compact ? 36 : 48,
                    },
                    "& .MuiAccordionSummary-content": {
                        my: compact ? 0.25 : 1,
                        alignItems: "center",
                        overflow: "hidden",
                        marginRight: compact ? 0.5 : 1,
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                        mr: compact ? -0.25 : 0,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                    }}
                >
                    <Typography
                        component="span"
                        variant={compact ? "body2" : "subtitle1"}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            fontWeight: 600,
                            color: ACCENT.light,
                            fontSize: compact ? "0.8125rem" : "0.95rem",
                            lineHeight: compact ? 1.25 : 1.35,
                            wordBreak: "break-word",
                        }}
                    >
                        {headingText}
                    </Typography>
                    {showStoryShare ? (
                        <Box
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                ml: "auto",
                            }}
                        >
                            <SocialShareDropdown
                                compact
                                puzzleTitle={shareLabel}
                                puzzleId={storyShareId}
                                puzzleType={storySharePuzzleType}
                                shareUrl={storyShareUrl}
                                shareMessage={t("puzzle.playCross.story-share-message", {
                                    title: shareLabel,
                                })}
                                tooltipTitle={t("puzzle.playCross.story-share-tooltip")}
                            />
                        </Box>
                    ) : null}
                </Box>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    pt: 0,
                    px: compact ? 1.25 : 2,
                    pb: compact ? 1.25 : 2,
                }}
            >
                {imageUrl ? (
                    <>
                        <Box
                            sx={{
                                position: "relative",
                                mb: 1.5,
                                borderRadius: 1,
                                overflow: "hidden",
                                bgcolor: "action.hover",
                            }}
                        >
                            <Box
                                component="img"
                                src={imageUrl}
                                alt=""
                                sx={{
                                    width: "100%",
                                    maxHeight: 200,
                                    objectFit: "contain",
                                    display: "block",
                                    verticalAlign: "top",
                                }}
                            />
                            <Tooltip
                                title={t(
                                    "puzzle.playCross.story-image-view-large",
                                    "View larger image"
                                )}
                                arrow
                            >
                                <IconButton
                                    type="button"
                                    size="small"
                                    onClick={() => setStoryImageDialogOpen(true)}
                                    aria-label={t(
                                        "puzzle.playCross.story-image-view-large",
                                        "View larger image"
                                    )}
                                    sx={{
                                        ...playPillButtonSx,
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                    }}
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {/* Puzzle navigation icons */}
                            {storyId && (showSlidingPuzzle || showJigsawPuzzle) && (
                                <Box sx={{ position: "absolute", bottom: 8, right: 8, display: "flex", gap: 0.5 }}>
                                    {showSlidingPuzzle && (
                                        <Tooltip title="Sliding Puzzle Quest" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    trackEvent("story_puzzle_play", {
                                                        story_id: storyId ?? undefined,
                                                        puzzle_type: "SLIDING",
                                                        source: "story_accordion",
                                                    });
                                                    navigate(`/puzzle/play-sliding/${storyId}`);
                                                }}
                                                sx={playPillButtonSx}
                                            >
                                                <ViewCarouselIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {showJigsawPuzzle && (
                                        <Tooltip title="Jigsaw Muse" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    trackEvent("story_puzzle_play", {
                                                        story_id: storyId ?? undefined,
                                                        puzzle_type: "JIGSAW",
                                                        source: "story_accordion",
                                                    });
                                                    navigate(`/puzzle/play-jigsaw/${storyId}`);
                                                }}
                                                sx={playPillButtonSx}
                                            >
                                                <ExtensionIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )}
                        </Box>
                        <Dialog
                            open={storyImageDialogOpen}
                            onClose={() => setStoryImageDialogOpen(false)}
                            maxWidth="lg"
                            fullWidth
                            PaperProps={{
                                sx: { bgcolor: "background.default" },
                            }}
                        >
                            <DialogContent sx={{ p: 1.5, position: "relative" }}>
                                <IconButton
                                    type="button"
                                    onClick={() => setStoryImageDialogOpen(false)}
                                    aria-label={t("puzzle.wordMeaning.close-aria")}
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        zIndex: 1,
                                        bgcolor: "background.paper",
                                        boxShadow: 1,
                                        "&:hover": { bgcolor: "background.paper" },
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    sx={{
                                        width: "100%",
                                        maxHeight: "min(85vh, 900px)",
                                        objectFit: "contain",
                                        display: "block",
                                        mx: "auto",
                                    }}
                                />
                            </DialogContent>
                        </Dialog>
                    </>
                ) : null}
                {children != null ? (
                    <Typography
                        component="div"
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}
                    >
                        {children}
                    </Typography>
                ) : null}
                {imageUrl && getAppCopyrightLine() ? (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.35 }}
                        >
                            {getAppCopyrightLine()}
                        </Typography>
                    </Box>
                ) : null}
            </AccordionDetails>
        </Accordion>
    );
}
